import styled from "styled-components";
import { ROW_HEIGHT, TEXT_SCALE_PROPS } from "@lhd/constants";
import { DISPLAY_SIZES } from "@lhd/types";

export const MIN_HEADER_HEIGHT = 29;

export const getTableDivHeaderHeight = (
  displaySize?: TEXT_SCALE_PROPS["displaySize"],
) => {
  const size = displaySize || DISPLAY_SIZES.SMALL;
  return Math.max(MIN_HEADER_HEIGHT, ROW_HEIGHT[size]);
};

export const Styles = styled.div<{
  gridTemplateColumns: string;
  headerHeight: number;
  heightScrollBar: number;
}>`
  /* ===============================
     CSS Variables (easy to theme)
     =============================== */
  --table-border-color: #e5e7eb;
  --table-header-bg: #fafafa;
  --table-row-hover-bg: #f5f5f5;
  --table-bg: #ffffff;
  --table-height-header-row: ${({ headerHeight }) => `${headerHeight}px`};
  --table-total-height-header-row: ${({ headerHeight, heightScrollBar }) =>
    `${headerHeight + heightScrollBar}px`};
  /* ===============================
     Table wrapper
     =============================== */
  overflow: auto;
  position: relative;
  /* Chrome, Safari, Edge */
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  /* Prevent scroll jumping when scrollbar is added */
  overflow-anchor: none;

  /* Firefox */
  scrollbar-width: none;

  /* IE / Edge cũ */
  -ms-overflow-style: none;

  background: var(--table-bg);
  border: 1px solid var(--table-border-color);

  [data-dnd-kit-overlay] .table-div__row {
    border: 1px solid var(--table-border-color);
    background: white;
  }

  /* ===============================
     Header & Row (shared grid layout)
     =============================== */

  .table-div__header {
    position: sticky;
    top: 0;
    z-index: 106;
    height: var(--table-height-header-row);
    .table-div__cell {
      border-bottom: 1px solid var(--table-border-color);
    }
  }
  .table-div__header,
  .table-div__row {
    display: grid;
    align-items: center;
    width: fit-content;
    grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns};
  }

  /* ===============================
     Cell (th / td)
     =============================== */
  .table-div__cell.fixed-left {
    overflow: hidden;
  }
  .table-div__cell {
    box-sizing: border-box;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--table-cell-bg, #fff);
    /* Text handling */
    white-space: nowrap;
    text-overflow: ellipsis;
    padding: var(--table-cell-padding, 0);
    /* Cell borders */
    border-right: 1px solid var(--table-border-color);
    border-top: 1px solid var(--table-border-color);
    &.last_item {
      border-bottom: 1px solid var(--table-border-color);
    }
    &.scroll_cell {
      padding: 0;
    }
  }
  .row_scroll {
    border-top: 1px solid var(--table-border-color);
  }

  /* Remove right border for last column */
  .table-div__cell:last-child {
    border-right: none;
  }

  /* ===============================
     Header style
     =============================== */
  .table-div__header {
    font-weight: 600;
    background: var(--table-header-bg);
  }

  .table-div__cell_header_inner {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .table-div__col-drag,
  .table-div__col-drag-custom {
    cursor: grab;
    user-select: none;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .table-div__col-drag-custom {
    cursor: default;
  }

  .table-div__col-drag:active {
    cursor: grabbing;
  }

  .table-div__col-resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 6px;
    height: 100%;
    cursor: col-resize;
    z-index: 2;
    touch-action: none;
  }

  .table-div__col-resize-handle:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  .table-div__cell_header {
    position: relative;
  }

  /* ===============================
     Row hover effect
     =============================== */
  .table-div__row:hover .table-div__cell {
    background: var(--table-row-hover-bg);
  }

  .ant-form-item-explain {
    white-space: break-spaces;
  }

  .ant-empty {
    font-size: inherit !important;
    .ant-empty-description {
      font-size: inherit !important;
      color: #777777;
    }
  }
  .table-div__layout-row {
    min-height: 40px;
  }

  .table-div__layout-row .table-div__cell {
    border-bottom: 1px solid #d9d9d9;
    box-sizing: border-box;
    justify-content: start;
    padding: 0 4px;
  }

  .table-div__cell {
    position: relative;
  }

  .table-div__col-resize-handle-custom {
    position: absolute;
    top: 0;
    right: -3px;
    width: 6px;
    height: 100%;
    cursor: ew-resize;
    z-index: 10;
    touch-action: none;
    background: transparent !important;
    background-image: none !important;
  }

  .table-div__col-resize-handle::before,
  .table-div__col-resize-handle::after {
    content: none !important;
    display: none !important;
  }

  .table-div__resize-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 1;
    color: #666;
    font-size: 16px;
    line-height: 1;
    pointer-events: none;
  }

  .table-div__col-resize-handle:hover {
    background: transparent !important;
  }
  .empty-notification-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    background-color: #ffffff;
    width: 100%;
    margin: 1rem 0;
  }

  .empty-notification-icon-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    margin-bottom: 0.5rem;
  }

  .empty-notification-icon {
    padding: 0.375rem;
    background-color: #eff6ff;
    color: #2563eb;
    border-radius: 0.375rem;
  }

  .empty-notification-content {
    text-align: center;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    padding: 2rem;
    background-color: rgb(250, 250, 250);
  }

  .empty-notification-title {
    color: #073677c1;
    font-weight: 500;
    font-size: 20px;
    margin-bottom: 0.25rem;
  }

  .empty-notification-subtitle {
    color: #4271b3c1;
    font-size: 0.875rem;
  }
`;
